import { Form, useNavigate } from '@remix-run/react';
import { PanelBottomClose, PanelBottomOpen, SearchIcon } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { SearchedFilterBadges } from '~/components/SearchedFilterBadges';
import { SearchFilter } from '~/components/SearchFilter';
import { Button, buttonVariants } from '~/components/ui/button';

interface Props {
  brands: Brand[];
  tags: Tag[];
  initialQuery?: string;
  initialBrands?: string[];
  initialTags?: string[];
}

interface Brand {
  id: string;
  name: string;
}

interface Tag {
  id: string;
  tag: string;
}

export const Search = ({
  brands,
  tags,
  initialQuery = '',
  initialBrands = [],
  initialTags = [],
}: Props) => {
  const initialShowFilter = useCallback((): boolean => {
    if (initialTags.length > 0 || initialBrands.length > 0) {
      // return true;
      return false;
    }
    return false;
  }, [initialTags, initialBrands]);

  const [selectedTags, setSelectedTags] = useState<string[]>(initialTags);
  const [selectedBrands, setSelectedBrands] = useState<string[]>(initialBrands);
  const [inputQuery, setInputQuery] = useState<string>(initialQuery);
  const [showFilter, setShowFilter] = useState<boolean>(initialShowFilter());
  const navigate = useNavigate();

  // パラメータが変わったら値を更新
  useEffect(() => {
    setSelectedTags(initialTags);
    setShowFilter(initialShowFilter());
  }, [initialTags, initialShowFilter]);
  useEffect(() => {
    setSelectedBrands(initialBrands);
    setShowFilter(initialShowFilter());
  }, [initialBrands, initialShowFilter]);
  useEffect(() => {
    setInputQuery(initialQuery);
    setShowFilter(initialShowFilter());
  }, [initialQuery, initialShowFilter]);

  const handleTagChange = (tagId: string) => {
    setSelectedTags(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  const handleBrandChange = (brandId: string) => {
    setSelectedBrands(prev =>
      prev.includes(brandId) ? prev.filter(id => id !== brandId) : [...prev, brandId]
    );
  };

  const onSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    console.dir(navigate);

    const queryParams = new URLSearchParams();

    if (inputQuery) {
      queryParams.append('query', inputQuery);
    }

    if (selectedTags.length > 0) {
      queryParams.append('tags', selectedTags.join(','));
    }

    if (selectedBrands.length > 0) {
      queryParams.append('brands', selectedBrands.join(','));
    }

    navigate(`/pedals?${queryParams.toString()}`);
  };

  return (
    <>
      <Form onSubmit={onSearch}>
        <div className="flex flex-col scroll-mt-14 md:w-full lg:w-2/3 xl:w-1/2">
          <div className="flex flex-col gap-[4px]">
            <div className="flex flex-wrap gap-x-2 w-full">
              <FilterButton showFilter={showFilter} setShowFilter={setShowFilter} />

              <div className="relative flex-1">
                <input
                  className="rounded-md border outline-neutral-300 px-3 py-2 text-[13px]/tight text-neutral-600 font-medium placeholder:text-inherit placeholder:opacity-50 !pr-12 w-full"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  placeholder="キーワードで検索"
                  spellCheck="false"
                  maxLength={512}
                  name="query"
                  value={inputQuery || ''}
                  onChange={(e) => setInputQuery(e.target.value)}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                  <button type="submit" className="opacity-60 hover:opacity-100">
                    <SearchIcon height={16} width={16} className='' />
                  </button>
                </div>
              </div>

              {/* この div は md サイズ以上の時だけ表示 */}
              <div className="hidden md:flex">
                <SearchButton width={'w-[70px]'} />
              </div>
            </div>

            {/* md サイズ以下の時に表示される検索ボタン */}
            <div className="flex md:hidden mt-2">
              <SearchButton width={'w-full'} />
            </div>
          </div>
        </div>

        {showFilter && (
          <div className="mt-3">
            <SearchFilter
              brands={brands}
              tags={tags}
              selectedTags={selectedTags}
              selectedBrands={selectedBrands}
              onTagChange={handleTagChange}
              onBrandChange={handleBrandChange}
            />
          </div>
        )}
      </Form>

      <div className={'mt-3'}>
        <SearchedFilterBadges
          query={initialQuery}
          brands={brands}
          tags={tags}
          selectedTags={initialTags}
          selectedBrands={initialBrands}
        />
      </div>
    </>
  );
};


interface FilterButtonProps {
  showFilter: boolean;
  setShowFilter: (showFilter: boolean) => void;
}
const FilterButton = ({showFilter, setShowFilter}: FilterButtonProps) => {
  return (
    <Button
      type='button'
      className={`${buttonVariants({
        variant: 'outline',
      })} gap-[0.75ch] text-neutral-400 h-[34.25px]`}
      onClick={() => setShowFilter(!showFilter)}
    >
      {!showFilter && (
        <PanelBottomClose height={18} width={18} className='text-neutral-400' />

      )}
      {showFilter && (
        <PanelBottomOpen height={18} width={18} className='text-neutral-700' />
      )}
      <span className="text-[13px]/tight">タグ・ブランド</span>
    </Button>
  )
}

interface SearchButtonProps {
  width: 'w-[70px]' | 'w-full'
}
const SearchButton = ({ width }: SearchButtonProps) => {
  return (
    <Button
      type="submit"
      className={`${buttonVariants({
        variant: 'default',
      })} gap-[0.75ch] h-[34.25px] ${width}`}
    >
      {/* <SearchIcon height={16} width={16} className='' /> */}
      <span className="text-[13px]/tight">検{' '}索</span>
    </Button>
  )
}